import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StarRating } from "@/components/StarRating";
import { MapPin, Clock, Check, Award } from "lucide-react";

const DEFAULT_TIME_SLOTS = ["10:00", "12:00", "14:00", "16:00", "18:00"];

export default function PractitionerDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [p, setP] = useState(null);
  const [date, setDate] = useState("");
  const [slot, setSlot] = useState("");
  const [availableSlots, setAvailableSlots] = useState([]);
  const [sessionType, setSessionType] = useState("");
  const [duration, setDuration] = useState(60);
  const [notes, setNotes] = useState("");
  const [buyPackage, setBuyPackage] = useState(false);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [membersOnly, setMembersOnly] = useState(false);
  const [reviews, setReviews] = useState(null);
  const [feePct, setFeePct] = useState(0);

  useEffect(() => {
    api.get(`/practitioners/${id}`).then((r) => {
      setP(r.data);
      if (r.data.session_types?.length) setSessionType(r.data.session_types[0]);
    }).catch((e) => { if (e.response?.status === 403) setMembersOnly(true); });
    api.get("/settings/fees").then((r) => setFeePct(Number(r.data.customer_fee_pct) || 0)).catch(() => {});
    api.get(`/practitioners/${id}/reviews`).then((r) => setReviews(r.data)).catch(() => setReviews({ reviews: [], review_count: 0 }));
    // default date = tomorrow
    const t = new Date();
    t.setDate(t.getDate() + 1);
    setDate(t.toISOString().split("T")[0]);
  }, [id]);

  // Load availability for the selected date
  useEffect(() => {
    if (!id || !date) return;
    api.get(`/practitioners/${id}/availability?date=${date}`)
      .then((r) => {
        const slots = r.data.slots || [];
        setAvailableSlots(slots);
        setSlot("");
      })
      .catch(() => setAvailableSlots([]));
  }, [id, date]);

  if (membersOnly) return (
    <div className="max-w-xl mx-auto py-24 px-6 text-center" data-testid="founder-members-only">
      <div className="text-xs uppercase tracking-[0.28em] text-[#E58B5E]">Members only</div>
      <h1 className="font-serif text-4xl mt-4 text-[#F7ECEB]">Founder coaching unlocks with membership.</h1>
      <p className="text-[#A89896] mt-4">Join as a Member, Premium or VIP to view Hilda's profile and book a session.</p>
      <Link to="/membership" className="btn-primary inline-block mt-8" data-testid="founder-members-only-cta">View membership</Link>
    </div>
  );
  if (!p) return <div className="max-w-4xl mx-auto py-20 px-6 text-center text-[#A89896]">Loading...</div>;

  const submit = async () => {
    setErr("");
    if (!user) { nav("/login", { state: { from: `/practitioners/${id}` } }); return; }
    if (!date || !slot || !sessionType) { setErr("Please choose date, time and session type."); return; }
    setLoading(true);
    try {
      const { data: booking } = await api.post("/bookings", {
        practitioner_id: id,
        date, time_slot: slot, session_type: sessionType,
        duration_minutes: duration, notes,
      });
      // VIP unlimited coaching or free-status booking → straight to dashboard
      if (booking.status === "confirmed" && Number(booking.amount) === 0) {
        nav("/dashboard");
        return;
      }
      const { data: session } = await api.post("/payments/checkout", {
        origin_url: window.location.origin,
        booking_id: booking.id,
        package: buyPackage,
      });
      window.location.href = session.checkout_url;
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || "Could not create booking");
    } finally { setLoading(false); }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 grid lg:grid-cols-12 gap-12" data-testid="practitioner-detail">
      <div className="lg:col-span-6 min-w-0">
        <div className="aspect-[4/5] rounded-3xl overflow-hidden">
          <img src={p.image_url} alt={p.name} className="w-full h-full object-cover" />
        </div>
      </div>
      <div className="lg:col-span-6 min-w-0">
        {p.is_founder && (
          <div className="inline-block text-xs uppercase tracking-[0.2em] bg-[#E58B5E] text-white px-3 py-1 rounded-full mb-4">
            Founder · Premium & VIP only
          </div>
        )}
        <h1 className="font-serif text-4xl sm:text-5xl text-[#F7ECEB] break-words">{p.name}</h1>
        <div className="text-[#C93B3B] mt-2">{p.title}</div>
        <div className="flex flex-wrap items-center gap-4 mt-4 text-sm text-[#A89896]">
          <span className="flex items-center gap-1"><MapPin className="w-4 h-4" /> {p.location}</span>
          <span className="flex items-center gap-1"><Clock className="w-4 h-4" /> {p.years_experience} yrs experience</span>
          <StarRating value={p.avg_rating} count={p.review_count} testId="detail-rating" />
        </div>

        <p className="mt-8 text-[#F7ECEB] leading-relaxed">{p.bio}</p>

        {p.credentials && (
          <div className="mt-4 flex items-start gap-2 text-sm text-[#A89896]">
            <Award className="w-4 h-4 mt-0.5 text-[#E58B5E]" />
            <span>{p.credentials}</span>
          </div>
        )}

        <div className="mt-6 flex flex-wrap gap-2">
          {p.specialties.map((s) => (
            <span key={s} className="text-xs px-3 py-1 rounded-full bg-[#1F1717] border border-[#382A2A] text-[#C93B3B]">
              {s}
            </span>
          ))}
        </div>

        <div className="mt-10 p-8 tt-card" data-testid="booking-form">
          <div className="text-xs uppercase tracking-[0.2em] text-[#C93B3B]">Book a session</div>
          <div className="font-serif text-3xl mt-2 text-[#F7ECEB]">${p.hourly_rate}<span className="text-lg text-[#A89896]"> /hr</span></div>

          {p.package_price && (
            <div className="mt-4 p-4 rounded-xl bg-[#110D0D] border border-[#382A2A]">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={buyPackage}
                  onChange={(e) => setBuyPackage(e.target.checked)}
                  data-testid="buy-package-toggle"
                  className="mt-1 accent-[#C93B3B]"
                />
                <div>
                  <div className="text-sm font-medium text-[#F7ECEB]">
                    Purchase 10-session package · ${p.package_price.toFixed(0)}
                  </div>
                  <div className="text-xs text-[#A89896] mt-1">
                    Save ${((p.hourly_rate * 10) - p.package_price).toFixed(0)} vs booking individually.
                  </div>
                </div>
              </label>
            </div>
          )}

          <div className="mt-6 grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-[#C93B3B]">Date</label>
              <input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="booking-date" className="w-full mt-2 px-3 py-2 rounded-lg border border-[#382A2A] bg-[#1F1717]" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-[#C93B3B]">Session type</label>
              <select value={sessionType} onChange={(e) => setSessionType(e.target.value)} data-testid="booking-session-type" className="w-full mt-2 px-3 py-2 rounded-lg border border-[#382A2A] bg-[#1F1717]">
                {p.session_types.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>

          <div className="mt-6">
            <label className="text-xs uppercase tracking-[0.2em] text-[#C93B3B]">Time</label>
            {(() => {
              const slots = p.is_founder ? DEFAULT_TIME_SLOTS : availableSlots;
              if (slots.length === 0) {
                return (
                  <div className="mt-2 p-4 rounded-lg bg-[#110D0D] text-sm text-[#A89896]">
                    No availability on this date. Try another day.
                  </div>
                );
              }
              return (
                <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 mt-2">
                  {slots.map((t) => (
                    <button key={t} type="button" onClick={() => setSlot(t)} data-testid={`slot-${t}`}
                      className={`px-3 py-2 rounded-lg text-sm border transition-colors ${
                        slot === t
                          ? "bg-[#C93B3B] text-white border-[#C93B3B]"
                          : "bg-[#1F1717] text-[#F7ECEB] border-[#382A2A] hover:border-[#C93B3B]"
                      }`}>{t}</button>
                  ))}
                </div>
              );
            })()}
          </div>

          <div className="mt-6">
            <label className="text-xs uppercase tracking-[0.2em] text-[#C93B3B]">Duration</label>
            <div className="flex gap-2 mt-2">
              {[60, 90, 120].map((d) => (
                <button key={d} type="button" onClick={() => setDuration(d)}
                  data-testid={`duration-${d}`}
                  className={`px-4 py-2 rounded-lg text-sm border transition-colors ${
                    duration === d
                      ? "bg-[#C93B3B] text-white border-[#C93B3B]"
                      : "bg-[#1F1717] text-[#F7ECEB] border-[#382A2A] hover:border-[#C93B3B]"
                  }`}>{d} min</button>
              ))}
            </div>
          </div>

          <div className="mt-6">
            <label className="text-xs uppercase tracking-[0.2em] text-[#C93B3B]">Notes (optional)</label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="booking-notes"
              rows={3}
              className="w-full mt-2 px-3 py-2 rounded-lg border border-[#382A2A] bg-[#1F1717]" />
          </div>

          <div className="mt-6 flex flex-wrap items-center justify-between gap-4">
            {(() => {
              const base = buyPackage && p.package_price ? p.package_price : p.hourly_rate * duration / 60;
              const fee = base * feePct / 100;
              return (
                <div className="text-sm text-[#A89896]" data-testid="booking-total">
                  Total: <span className="font-serif text-2xl text-[#F7ECEB]">${(base + fee).toFixed(2)}</span>
                  {fee > 0 && <div className="text-xs mt-1">${base.toFixed(2)} session + ${fee.toFixed(2)} service fee ({feePct}%)</div>}
                </div>
              );
            })()}
            <div className="flex gap-2">
              {user && p.user_id && p.user_id !== user.id && (
                <Link to={`/messages?to=${p.user_id}&name=${encodeURIComponent(p.name)}`}
                  className="btn-ghost text-sm" data-testid="message-practitioner">
                  Message
                </Link>
              )}
              <button onClick={submit} disabled={loading} className="btn-primary" data-testid="booking-submit">
                {loading ? "Processing..." : (user ? (p.is_founder ? "Book & pay" : "Request & pay") : "Sign in to book")}
              </button>
            </div>
          </div>
          {!p.is_founder && (
            <p className="mt-3 text-xs text-[#A89896]" data-testid="hold-note">
              {p.name.split(" ")[0]} has 24 hours to accept your request. If it's declined or unanswered, you're refunded in full automatically.
            </p>
          )}

          {err && <div className="mt-4 text-sm text-red-700" data-testid="booking-error">{err}</div>}
          {p.is_founder && user && !["premium", "vip"].includes(user.membership_tier) && (
            <div className="mt-4 text-sm text-[#A89896] bg-[#110D0D] p-4 rounded-lg flex items-start gap-2">
              <Check className="w-4 h-4 text-[#E58B5E] mt-0.5" />
              <span>Founder coaching requires Premium or VIP membership. <Link to="/membership" className="text-[#C93B3B] underline">Upgrade</Link></span>
            </div>
          )}
        </div>

        <div className="mt-12" data-testid="reviews-section">
          <div className="flex items-center justify-between">
            <h2 className="font-serif text-3xl text-[#F7ECEB]">Reviews</h2>
            <StarRating value={reviews?.avg_rating} count={reviews?.review_count} />
          </div>
          {!reviews || reviews.reviews.length === 0 ? (
            <p className="text-sm text-[#A89896] mt-4">No reviews yet. Verified members can leave one after a completed session.</p>
          ) : (
            <div className="mt-6 space-y-4">
              {reviews.reviews.map((r) => (
                <div key={r.id} className="tt-card p-5" data-testid={`review-${r.id}`}>
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-medium text-[#F7ECEB]">{r.author_name}</div>
                    <StarRating value={r.rating} showCount={false} size="w-3.5 h-3.5" />
                  </div>
                  <p className="text-sm text-[#A89896] mt-2 leading-relaxed">{r.content}</p>
                  <div className="text-[10px] uppercase tracking-wider text-[#A89896] mt-3">Verified session · {r.created_at?.slice(0, 10)}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
